/*
 * Atolyewolf – hediye paketi
 *
 * 1) <aw-gift-wrap> (snippets/aw-gift-wrap.liquid): ürün sayfasındaki kart. Seçiliyken ana ürün sepete eklendikten sonra
 *    ("variant:add" ya da "aw:gift-parent-add") hediye paketi ürünü ayrı satır olarak eklenir. Paket satırı "_aw_gift_parent" gizli özelliğiyle
 *    ana ürünün satır anahtarına bağlanır; ürün adı ve hediye notu görünür özellik olarak siparişe düşer.
 * 2) Sepet senkronu: ana ürün sepetten çıkarılır/azaltılırsa bağlı paket adedi de düşürülür (hiçbir zaman artırılmaz).
 *    Paket satırı sepetten silinirse ana üründeki "Hediye Paketi: Evet" özelliği de kaldırılır: ana ürün gizli
 *    "_aw_gift" özelliğinde görünür özelliğin adını taşır.
 * 3) Blokta video seçiliyse "videoyu izle" penceresi; iframe yalnızca pencere açıkken yüklenir (kapanınca durur).
 */

const GIFT_PARENT_PROPERTY = '_aw_gift_parent';
const GIFT_MARKER_PROPERTY = '_aw_gift';

// v4 öncesi sepetlerde "_aw_gift" işareti yok → bilinen özellik adı/değer çiftleriyle eşleştirilir
const LEGACY_GIFT_PROPERTIES = { 'Hediye Paketi': 'Evet', 'Gift wrapping': 'Yes' };

// Paket satırı eklenirken sepet senkronu beklemeli (ana ürün eklendi, paket henüz eklenmedi)
let awGiftPending = 0;

const cartRequest = async (path, body) => {
  const response = await fetch(`${window.Shopify?.routes?.root || '/'}${path}`, {
    method: body ? 'POST' : 'GET',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: body ? JSON.stringify(body) : undefined
  });

  const json = await response.json();

  if (!response.ok) {
    throw new Error(json.description || json.message || 'Cart error');
  }

  return json;
};

const refreshCart = () => {
  document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
};

class AwGiftWrap extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    this.toggle = this.querySelector('.aw-gift__toggle');
    this.note = this.querySelector('[data-aw-gift-note]');
    this.counter = this.querySelector('[data-aw-gift-counter]');
    this.detailsInner = this.querySelector('.aw-gift__details-inner');
    this.errorElement = this.querySelector('.aw-gift__error');
    this.dialog = this.querySelector('.aw-gift__dialog:not(.aw-gift__dialog--video)');
    this.videoDialog = this.querySelector('.aw-gift__dialog--video');

    this.toggle?.addEventListener('change', () => this.setOpen(this.toggle.checked), { signal });
    this.note?.addEventListener('input', () => this.updateCounter(), { signal });

    const thumb = this.querySelector('.aw-gift__thumb');
    const preloadDialogImage = () => {
      const image = this.dialog?.querySelector('img');

      if (image) {
        image.loading = 'eager';
      }
    };

    // Kapalı <dialog> içindeki lazy görsel açılışta geç yükleniyor → üzerine gelince / tıklayınca hemen yükle
    thumb?.addEventListener('pointerenter', preloadDialogImage, { signal, once: true });
    thumb?.addEventListener('focus', preloadDialogImage, { signal, once: true });
    thumb?.addEventListener('click', () => {
      preloadDialogImage();
      this.dialog?.showModal();
    }, { signal });
    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog || event.target.closest('[data-aw-gift-close]')) {
        this.dialog.close();
      }
    }, { signal });

    this.querySelector('[data-aw-gift-video-open]')?.addEventListener('click', () => this.openVideo(), { signal });
    this.videoDialog?.addEventListener('click', (event) => {
      if (event.target === this.videoDialog || event.target.closest('[data-aw-gift-close]')) {
        this.videoDialog.close();
      }
    }, { signal });
    this.videoDialog?.addEventListener('close', () => {
      this.videoDialog.querySelector('[data-aw-gift-video-frame]')?.replaceChildren();
    }, { signal });

    document.addEventListener('variant:add', (event) => this.onVariantAdd(event), { signal });
    // Kendi /cart/add.js isteğini yapan bloklar (ör. ekstra harf/charm) aynı detayla bu olayı yayınlar
    document.addEventListener('aw:gift-parent-add', (event) => this.onVariantAdd(event), { signal });

    // Geri/ileri ile dönüldüğünde tarayıcı anahtar durumunu geri yükleyebilir → paneli ve alanları yeniden eşitle
    window.addEventListener('pageshow', () => this.setOpen(Boolean(this.toggle?.checked)), { signal });

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.classList.add('is-revealed');
          this.observer.disconnect();
        }
      }, { threshold: 0.6 });

      this.observer.observe(this);
    }

    this.setOpen(Boolean(this.toggle?.checked));
    this.updateCounter();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.observer?.disconnect();
  }

  openVideo() {
    const frame = this.videoDialog?.querySelector('[data-aw-gift-video-frame]');

    if (!frame) return;

    if (!frame.querySelector('iframe')) {
      const iframe = document.createElement('iframe');
      iframe.src = frame.dataset.src;
      iframe.title = frame.dataset.title || '';
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      frame.append(iframe);
    }

    this.videoDialog.showModal();
  }

  get form() {
    return document.getElementById(this.getAttribute('form'));
  }

  setOpen(open) {
    this.toggleAttribute('open', open);
    this.toggle?.setAttribute('aria-expanded', String(open));

    if (this.detailsInner) {
      this.detailsInner.inert = !open;
    }

    this.querySelectorAll('[data-aw-gift-field]').forEach((field) => {
      field.disabled = !open;
    });

    // Hızlı ödeme butonu doğrudan ödemeye gider, paket satırı eklenemez → seçim açıkken gizlenir (CSS)
    this.form?.toggleAttribute('data-aw-gift-active', open);

    if (!open) {
      this.showError('');
    }
  }

  updateCounter() {
    if (this.counter && this.note) {
      this.counter.textContent = `${this.note.value.length}/${this.note.maxLength}`;
    }
  }

  showError(message) {
    if (!this.errorElement) return;

    this.errorElement.textContent = message;
    this.errorElement.hidden = !message;
  }

  async onVariantAdd(event) {
    const form = this.form;

    if (!this.toggle?.checked || !form || !(event.target instanceof Node)) return;
    if (event.target !== form && !event.target.contains?.(form)) return;

    const parent = event.detail?.items?.[0];
    const cart = event.detail?.cart;

    if (!parent?.key) return;

    const parentLine = cart?.items?.find((item) => item.key === parent.key);
    const parentQuantity = parentLine ? parentLine.quantity : (parent.quantity || 1);
    const existingQuantity = (cart?.items || [])
      .filter((item) => item.properties?.[GIFT_PARENT_PROPERTY] === parent.key)
      .reduce((total, item) => total + item.quantity, 0);

    const quantity = parentQuantity - existingQuantity;

    if (quantity <= 0) return;

    const productName = [parent.product_title, parent.variant_title].filter(Boolean).join(' – ');
    const properties = {
      [this.dataset.productLabel || 'For']: productName,
      [GIFT_PARENT_PROPERTY]: parent.key
    };

    const note = this.note?.value.trim();

    if (note) {
      properties[this.dataset.noteLabel || 'Gift note'] = note;
    }

    let failed = false;
    awGiftPending += 1;

    try {
      await cartRequest('cart/add.js', {
        items: [{ id: Number(this.dataset.variantId), quantity, properties }]
      });

      this.showError('');
    } catch (error) {
      this.showError(error.message);
      failed = true;
    } finally {
      awGiftPending -= 1;
    }

    if (failed) {
      // Paket eklenemediyse ana üründeki "Hediye Paketi" özelliği de kalmasın
      await cartRequest('cart.js').then((latestCart) => syncGiftLines(latestCart)).catch(() => {});
    }

    refreshCart();
  }
}

if (!window.customElements.get('aw-gift-wrap')) {
  window.customElements.define('aw-gift-wrap', AwGiftWrap);
}

/* Sepet senkronu */

let awGiftSyncing = false;

const giftPropertyName = (item) => {
  const properties = item.properties || {};
  const marker = properties[GIFT_MARKER_PROPERTY];

  if (marker && Object.prototype.hasOwnProperty.call(properties, marker)) {
    return marker;
  }

  return Object.keys(LEGACY_GIFT_PROPERTIES).find((name) => properties[name] === LEGACY_GIFT_PROPERTIES[name]) || null;
};

const syncGiftLines = async (cart, { reloadOnChange = false } = {}) => {
  if (awGiftSyncing || awGiftPending > 0 || !cart?.items?.length) return;

  const linesByKey = new Map(cart.items.map((item) => [item.key, item]));
  const giftLinesByParent = new Map();

  cart.items.forEach((item) => {
    const parentKey = item.properties?.[GIFT_PARENT_PROPERTY];

    if (parentKey) {
      giftLinesByParent.set(parentKey, [...(giftLinesByParent.get(parentKey) || []), item]);
    }
  });

  // 1) Paket adedi ana ürün adedini geçemez (ana ürün silindiyse paket de silinir)
  const updates = {};
  const remainingGiftQuantity = new Map();

  giftLinesByParent.forEach((giftLines, parentKey) => {
    let allowed = linesByKey.get(parentKey)?.quantity || 0;
    let remaining = 0;

    giftLines.forEach((giftLine) => {
      const quantity = Math.min(giftLine.quantity, allowed);
      allowed -= quantity;
      remaining += quantity;

      if (quantity !== giftLine.quantity) {
        updates[giftLine.key] = quantity;
      }
    });

    remainingGiftQuantity.set(parentKey, remaining);
  });

  // 2) Paketi silinmiş ana ürünlerden "Hediye Paketi: Evet" özelliğini kaldır
  const strips = cart.items.flatMap((item) => {
    if (item.properties?.[GIFT_PARENT_PROPERTY] || remainingGiftQuantity.get(item.key) > 0) return [];

    const propertyName = giftPropertyName(item);
    const hasMarker = Boolean(item.properties?.[GIFT_MARKER_PROPERTY]);

    if (!propertyName && !hasMarker) return [];

    // Shopify /cart/change.js özellik anahtarını silmez (yalnızca boş bırakır) → satır özelliksiz yeniden eklenir
    const properties = Object.fromEntries(Object.entries(item.properties || {}).filter(([name, value]) => {
      return name !== GIFT_MARKER_PROPERTY && name !== propertyName && value !== null && value !== '';
    }));

    const replacement = { id: item.variant_id, quantity: item.quantity, properties };

    if (item.selling_plan_allocation?.selling_plan?.id) {
      replacement.selling_plan = item.selling_plan_allocation.selling_plan.id;
    }

    return [{ key: item.key, replacement, original: { ...replacement, properties: item.properties } }];
  });

  if (Object.keys(updates).length === 0 && strips.length === 0) return;

  awGiftSyncing = true;

  try {
    if (Object.keys(updates).length > 0) {
      await cartRequest('cart/update.js', { updates });
    }

    for (const strip of strips) {
      await cartRequest('cart/change.js', { id: strip.key, quantity: 0 });

      try {
        await cartRequest('cart/add.js', { items: [strip.replacement] });
      } catch (error) {
        // Yeniden eklenemezse (ör. stok) ürün sepetten düşmesin: eski haliyle geri eklemeyi dene
        await cartRequest('cart/add.js', { items: [strip.original] }).catch(() => {});
        throw error;
      }
    }

    if (reloadOnChange) {
      window.location.reload();
    } else {
      refreshCart();
    }
  } catch (error) {
    console.warn('[aw-gift-wrap] cart sync failed', error);
  } finally {
    awGiftSyncing = false;
  }
};

document.addEventListener('cart:change', (event) => {
  if (event.detail?.baseEvent !== 'variant:add') {
    syncGiftLines(event.detail?.cart);
  }
});

// Sepet sayfasında tema adet değişiminde sayfayı yeniler (cart:change yayınlanmaz) → açılışta kontrol et
if (window.themeVariables?.settings?.pageType === 'cart') {
  cartRequest('cart.js').then((cart) => syncGiftLines(cart, { reloadOnChange: true })).catch(() => {});
}
